import { useEffect, useMemo, useState } from "react";

import { btn } from "@/components/Page";

const stopReasonText = (value) => value || "Waiting for the next offline opportunity.";

const formatRemaining = (seconds) => {
  const safe = Math.max(0, Number(seconds || 0));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  return `${hours}h ${minutes}m`;
};

export const MiningAutoConfigPanel = ({ autoMine, report, mines, saving, onSave }) => {
  const unlockedMines = useMemo(() => (mines || []).filter((mine) => mine.unlocked), [mines]);
  const [enabled, setEnabled] = useState(Boolean(autoMine?.enabled));
  const [mineId, setMineId] = useState(autoMine?.mine_id || unlockedMines[0]?.id || "");
  const [threshold, setThreshold] = useState(Number(autoMine?.energy_threshold || 40));

  useEffect(() => {
    setEnabled(Boolean(autoMine?.enabled));
    setMineId(autoMine?.mine_id || unlockedMines[0]?.id || "");
    setThreshold(Number(autoMine?.energy_threshold || 40));
  }, [autoMine?.enabled, autoMine?.mine_id, autoMine?.energy_threshold, unlockedMines]);

  const selectedMine = unlockedMines.find((mine) => mine.id === mineId) || unlockedMines[0] || null;

  const submit = (event) => {
    event.preventDefault();
    if (!selectedMine) return;
    onSave({
      enabled,
      mineId: selectedMine.id,
      energyThreshold: Number.isFinite(Number(threshold)) ? Number(threshold) : 0,
    });
  };

  return (
    <section data-testid="mining-auto-config-panel" className="border border-amber-900/30 bg-zinc-950/65 rounded-sm p-4 sm:p-5">
      <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">Offline Auto-Mining</div>
          <h2 className="mt-1 font-cinzel text-xl text-amber-200">Keep your crew digging while you are away.</h2>
          <p className="mt-2 max-w-3xl text-sm text-zinc-400">
            Choose one mine and an energy threshold. Offline runs earn <span className="text-zinc-100">50% XP and ore</span>, discoveries roll at <span className="text-zinc-100">50%</span> chance, and pickaxes lose <span className="text-zinc-100">65% less durability</span>.
          </p>
          <p data-testid="mining-auto-runtime-copy" className="mt-2 text-xs uppercase tracking-[0.18em] text-amber-300/90">
            Offline auto-mining only runs for 24 hours from the moment you enable it.
          </p>
        </div>
        <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">
          {autoMine?.enabled ? `Enabled • ${formatRemaining(autoMine?.remaining_seconds)} left` : "Disabled"}
        </div>
      </div>

      <form onSubmit={submit} className="mt-5 grid gap-4 lg:grid-cols-[1.2fr_1fr_auto] lg:items-end">
        <label className="text-[11px] uppercase tracking-[0.22em] text-zinc-500">
          Offline Mine
          <select
            data-testid="mining-auto-mine-select"
            value={mineId}
            onChange={(event) => setMineId(event.target.value)}
            className="mt-2 w-full rounded-sm border border-zinc-700 bg-zinc-900 px-3 py-2.5 text-sm text-zinc-100 outline-none focus:border-amber-500"
          >
            {unlockedMines.map((mine) => (
              <option key={mine.id} value={mine.id}>{mine.name}</option>
            ))}
          </select>
        </label>

        <label className="text-[11px] uppercase tracking-[0.22em] text-zinc-500">
          Start When Energy Is At Least
          <input
            data-testid="mining-auto-threshold-input"
            type="number"
            min={0}
            max={100}
            value={threshold}
            onChange={(event) => setThreshold(event.target.value)}
            className="mt-2 w-full rounded-sm border border-zinc-700 bg-zinc-900 px-3 py-2.5 text-sm text-zinc-100 outline-none focus:border-amber-500"
          />
        </label>

        <div className="flex flex-col gap-2 sm:flex-row lg:flex-col lg:items-stretch">
          <button
            type="button"
            data-testid="mining-auto-toggle-button"
            onClick={() => setEnabled((value) => !value)}
            className={`${btn("ghost")} justify-center ${enabled ? "border-emerald-600/60 text-emerald-300" : "border-zinc-700 text-zinc-400"}`}
          >
            {enabled ? "Enabled" : "Disabled"}
          </button>
          <button
            type="submit"
            data-testid="mining-auto-save-button"
            disabled={saving || !selectedMine}
            className={`${btn()} justify-center disabled:opacity-50`}
          >
            {saving ? "Saving" : "Save Settings"}
          </button>
        </div>
      </form>

      {selectedMine && (
        <div data-testid="mining-auto-selected-summary" className="mt-4 grid gap-3 rounded-sm border border-zinc-800 bg-zinc-900/40 p-3 text-sm text-zinc-300 sm:grid-cols-3">
          <div>Mine: <span className="text-zinc-100">{selectedMine.name}</span></div>
          <div>Energy per run: <span className="text-zinc-100">{selectedMine.energy_cost}</span></div>
          <div>Duration: <span className="text-zinc-100">{selectedMine.duration_label}</span></div>
        </div>
      )}

      {autoMine?.enabled && autoMine?.active_until && (
        <div data-testid="mining-auto-active-until" className="mt-3 text-xs uppercase tracking-[0.18em] text-zinc-500">
          Active until: <span className="text-zinc-300">{new Date(autoMine.active_until).toLocaleString()}</span>
        </div>
      )}

      {report && (
        <div data-testid="mining-auto-report" className="mt-5 rounded-sm border border-amber-900/30 bg-amber-950/10 p-4">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
            <h3 className="font-cinzel text-lg text-amber-200">Latest Offline Report</h3>
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">{report.mine_name || "Unknown Mine"}</div>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-4 text-sm text-zinc-300">
            <div>Sessions completed: <span className="text-zinc-100">{report.sessions_completed}</span></div>
            <div>XP gained: <span className="text-zinc-100">{report.xp_gained}</span></div>
            <div>Durability lost: <span className="text-zinc-100">{report.durability_loss}</span></div>
            <div>Current run underway: <span className="text-zinc-100">{report.current_session_underway ? "Yes" : "No"}</span></div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2 text-xs uppercase tracking-[0.18em] text-zinc-500">
            {Object.entries(report.raw_ore_rewards || {})
              .filter(([, amount]) => Number(amount || 0) > 0)
              .map(([rarity, amount]) => (
                <span key={rarity} className="border border-zinc-700 bg-zinc-950/50 px-2 py-1 text-zinc-300">{rarity}: {amount}</span>
              ))}
            {!Object.values(report.raw_ore_rewards || {}).some((amount) => Number(amount || 0) > 0) && (
              <span className="border border-zinc-700 bg-zinc-950/50 px-2 py-1 text-zinc-400">No ore recovered</span>
            )}
          </div>
          <div className="mt-3 text-sm text-zinc-400">
            Discoveries: <span className="text-zinc-100">{(report.discoveries || []).map((entry) => entry.name).join(", ") || "None"}</span>
          </div>
          <div className="mt-2 text-xs text-amber-300">{stopReasonText(report.stop_reason)}</div>
        </div>
      )}
    </section>
  );
};